import {existsSync, mkdirSync, readFileSync, writeFileSync} from 'node:fs';
import {homedir} from 'node:os';
import {join} from 'node:path';
import chalk from 'chalk';
import {SUPPORTED_SHELLS} from './data.js';
import {TEMPLATES} from './templates.js';
import {detectShell, getShellProfile} from './utils.js';

interface CompletionOptions {
  shell: string;
  install: boolean;
  advanced: boolean;
}

function getCompletionDir(): string {
  return join(homedir(), '.rawi', 'completion');
}

function copyCompletionTemplate(shell: string): string {
  try {
    const templateContent = TEMPLATES[shell as keyof typeof TEMPLATES];

    if (!templateContent) {
      throw new Error(`Template not found for shell: ${shell}`);
    }

    const completionDir = getCompletionDir();
    mkdirSync(completionDir, {recursive: true});

    const destPath = join(completionDir, `rawi-completion.${shell}`);

    writeFileSync(destPath, templateContent);

    return destPath;
  } catch (error) {
    throw new Error(
      `Failed to copy completion template: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

function isCompletionLoaded(shell: string): boolean {
  try {
    const profile = getShellProfile(shell);
    if (!profile || !existsSync(profile)) {
      return false;
    }

    const profileContent = readFileSync(profile, 'utf8');
    return (
      profileContent.includes('# rawi completion') ||
      (profileContent.includes('source') &&
        profileContent.includes('.rawi/completion'))
    );
  } catch {
    return false;
  }
}

async function installCompletion(
  completionPath: string,
  shell: string,
): Promise<void> {
  const profile = getShellProfile(shell);
  if (!profile) {
    throw new Error(`Cannot determine profile file for shell: ${shell}`);
  }

  if (isCompletionLoaded(shell)) {
    console.log(chalk.yellow(`Completion already installed for ${shell}`));
    return;
  }

  const sourceCommand =
    shell === 'fish'
      ? `source ${completionPath}`
      : `[ -f "${completionPath}" ] && source "${completionPath}"`;

  const installBlock = `
# rawi completion
${sourceCommand}
`;

  const {appendFileSync} = await import('node:fs');
  appendFileSync(profile, installBlock);

  console.log(chalk.green(`✓ Installed completion to: ${profile}`));
  console.log(chalk.gray('Restart your shell or run:'));
  console.log(chalk.cyan(`  source ${profile}`));
}

export async function generateCompletionScript(
  options: CompletionOptions,
): Promise<void> {
  try {
    let shell = options.shell;

    if (shell === 'auto') {
      shell = detectShell();
      console.log(chalk.gray(`Detected shell: ${shell}`));
    }

    if (!SUPPORTED_SHELLS.includes(shell) || shell === 'auto') {
      console.error(chalk.red(`Unsupported shell: ${shell}`));
      console.log(
        chalk.gray(
          `Supported shells: ${SUPPORTED_SHELLS.filter((s) => s !== 'auto').join(', ')}`,
        ),
      );
      process.exit(1);
    }

    console.log(chalk.gray('Copying completion template...'));
    const completionPath = copyCompletionTemplate(shell);

    if (options.install) {
      await installCompletion(completionPath, shell);
    } else {
      console.log(
        chalk.green(`✓ Completion script copied to: ${completionPath}`),
      );
      console.log(chalk.gray('\nTo install manually:'));
      const profile = getShellProfile(shell);
      if (profile) {
        const sourceCmd =
          shell === 'fish'
            ? `echo 'source ${completionPath}' >> ${profile}`
            : `echo '[ -f "${completionPath}" ] && source "${completionPath}"' >> ${profile}`;
        console.log(chalk.cyan(`  ${sourceCmd}`));
        console.log(chalk.cyan(`  source ${profile}`));
      }
      console.log(chalk.gray('Or use --install to install automatically'));
    }
  } catch (error) {
    console.error(chalk.red('Failed to setup completion:'));
    console.error(
      chalk.red(error instanceof Error ? error.message : String(error)),
    );
    process.exit(1);
  }
}
